import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useTechnicalSheet,
  useCreateTechnicalSheet,
  useUpdateTechnicalSheet,
} from "@/hooks/useTechnicalSheets";
import { useCBMContacts } from "@/hooks/useCBMContacts";
import { useEvent } from "@/hooks/useEvents";
import { useQuote } from "@/hooks/useQuotes";
import { useToast } from "@/hooks/useToast";
import { useEffect, useMemo, useState } from "react";
import { useWizardNavigation } from "@/hooks/useWizardNavigation";
import { useAutoSave } from "@/hooks/useAutoSave";
import { WizardProgressIndicator } from "./WizardProgressIndicator";
import { LivePdfPreview } from "./LivePdfPreview";
import { ChevronLeft, ChevronRight, Save, Loader2 } from "lucide-react";

const technicalSheetSchema = z.object({
  responsible_contact_id: z.string().optional(),
  coordinator_contact_id: z.string().optional(),
  technical_contact_id: z.string().optional(),
  quote_reference: z.string().optional(),
  total_price: z.coerce.number().optional(),
  deposit_paid: z.coerce.number().optional(),
  balance_due: z.coerce.number().optional(),
  team_musicians_count: z.coerce.number().optional(),
  team_technicians_count: z.coerce.number().optional(),
  team_total: z.coerce.number().optional(),
  team_specialization: z.string().optional(),
  target_audience: z.string().optional(),
  desired_ambiance: z.string().optional(),
  accommodation_count: z.coerce.number().optional(),
  accommodation_distance: z.string().optional(),
  accommodation_type: z.string().optional(),
  accommodation_details: z.string().optional(),
  transport_note: z.string().optional(),
  meals_during_event: z.string().optional(),
  meals_after_event: z.string().optional(),
  storage_location: z.string().optional(),
  storage_duration: z.string().optional(),
  in_ears_singers_count: z.coerce.number().optional(),
  in_ears_musicians_count: z.coerce.number().optional(),
  projection_equipment_type: z.string().optional(),
  projection_hdmi_required: z.boolean().optional(),
  balances_duration: z.string().optional(),
  balances_timing: z.string().optional(),
  battery_provider: z.string().optional(),
  battery_specifications: z.string().optional(),
  press_kit_url: z.string().optional(),
  visual_assets_url: z.string().optional(),
  merchandising_required: z.boolean().optional(),
  arrival_time: z.string().optional(),
  soundcheck_time: z.string().optional(),
  performance_start: z.string().optional(),
  performance_end: z.string().optional(),
  teardown_time: z.string().optional(),
  audio_requirements: z.string().optional(),
  lighting_requirements: z.string().optional(),
  projection_requirements: z.string().optional(),
  section_team_enabled: z.boolean().optional(),
  section_schedule_enabled: z.boolean().optional(),
  section_logistics_enabled: z.boolean().optional(),
  section_technical_enabled: z.boolean().optional(),
  section_communication_enabled: z.boolean().optional(),
  section_confirmation_enabled: z.boolean().optional(),
});

type TechnicalSheetFormData = z.infer<typeof technicalSheetSchema>;

interface TechnicalSheetFormProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
}

export function TechnicalSheetForm({
  open,
  onClose,
  eventId,
}: TechnicalSheetFormProps) {
  const { data: existingSheet } = useTechnicalSheet(eventId);
  const { data: event } = useEvent(eventId);
  const { data: quote } = useQuote(eventId);
  const { data: cbmContacts } = useCBMContacts();
  const createSheet = useCreateTechnicalSheet();
  const updateSheet = useUpdateTechnicalSheet();
  const { toast } = useToast();

  const responsables = useMemo(
    () => cbmContacts?.filter((c) => c.role === "Responsable") || [],
    [cbmContacts],
  );
  const coordinateurs = useMemo(
    () => cbmContacts?.filter((c) => c.role === "Coordinateur") || [],
    [cbmContacts],
  );
  const techniciens = useMemo(
    () => cbmContacts?.filter((c) => c.role === "Technicien") || [],
    [cbmContacts],
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { isSubmitting, isDirty },
  } = useForm<TechnicalSheetFormData>({
    resolver: zodResolver(technicalSheetSchema),
    defaultValues: existingSheet || {},
  });

  const formValues = watch();

  const steps = [
    {
      label: "Contacts & Devis",
      enabled: formValues.section_team_enabled !== false,
    },
    { label: "Équipe", enabled: formValues.section_team_enabled !== false },
    {
      label: "Logistique",
      enabled: formValues.section_logistics_enabled !== false,
    },
    {
      label: "Technique",
      enabled: formValues.section_technical_enabled !== false,
    },
    {
      label: "Horaires",
      enabled: formValues.section_schedule_enabled !== false,
    },
  ];

  const { currentStep, goToStep, nextStep, prevStep, canGoNext, canGoPrev } =
    useWizardNavigation({
      totalSteps: steps.length,
    });

  const [hasAutoPopulated, setHasAutoPopulated] = useState(false);

  useEffect(() => {
    if (!existingSheet && event && quote && cbmContacts && !hasAutoPopulated) {
      if (responsables.length > 0) {
        setValue("responsible_contact_id", responsables[0].id);
      }
      if (coordinateurs.length > 0) {
        setValue("coordinator_contact_id", coordinateurs[0].id);
      }
      if (techniciens.length > 0) {
        setValue("technical_contact_id", techniciens[0].id);
      }

      setValue("quote_reference", `Devis ${event.name}`);
      setValue("total_price", quote.total_amount);

      const depositPayment = quote.payments?.find(
        (p: { payment_type: string }) => p.payment_type === "Deposit",
      );
      if (depositPayment) {
        setValue("deposit_paid", depositPayment.amount);
        setValue("balance_due", quote.total_amount - depositPayment.amount);
      }

      if (event.team) {
        const teamMembersCount = event.team.team_members?.length || 0;
        setValue("team_total", teamMembersCount);
        setValue("team_musicians_count", Math.max(teamMembersCount - 1, 0));
        setValue("team_technicians_count", 1);
        setValue("accommodation_count", teamMembersCount);
      }

      setValue("team_specialization", "louange");
      setValue("accommodation_distance", "15 minutes maximum");
      setValue(
        "accommodation_type",
        "Chambres individuelles en hôtel 2-3 étoiles",
      );
      setValue(
        "transport_note",
        "Nous gérons 100% nos déplacements et nos véhicules. Vous n'avez rien à prévoir de ce côté.",
      );
      setValue("meals_during_event", "Eau pour les musiciens (à disposition)");
      setValue(
        "meals_after_event",
        "Collation légère (fruits, fromage, charcuterie, soft) OU repas chaud (selon horaire)",
      );
      setValue("balances_duration", "1 heure minimale");
      setValue(
        "balances_timing",
        "J0 après-midi OU jour avant (avec garantie de sauvegarde paramétrages)",
      );
      setValue("battery_provider", "Organisateur");
      setValue("projection_equipment_type", "Écran LED ou rétro-projecteur");
      setValue("projection_hdmi_required", true);
      setValue("merchandising_required", true);

      setValue("section_team_enabled", true);
      setValue("section_schedule_enabled", true);
      setValue("section_logistics_enabled", true);
      setValue("section_technical_enabled", true);
      setValue("section_communication_enabled", false);
      setValue("section_confirmation_enabled", true);

      setHasAutoPopulated(true);
    }
  }, [
    existingSheet,
    event,
    quote,
    cbmContacts,
    responsables,
    coordinateurs,
    techniciens,
    setValue,
    hasAutoPopulated,
  ]);

  const saveSheet = async (data: TechnicalSheetFormData) => {
    const payload = {
      ...data,
      event_id: eventId,
    };

    if (existingSheet) {
      await updateSheet.mutateAsync({
        id: existingSheet.id,
        updates: payload,
      });
    } else {
      await createSheet.mutateAsync(payload);
    }
  };

  const { isSaving, lastSaved } = useAutoSave({
    data: formValues,
    onSave: saveSheet,
    delay: 800,
    enabled: isDirty && open,
  });

  const onSubmit = async (data: TechnicalSheetFormData) => {
    try {
      await saveSheet(data);
      toast({ title: "Fiche technique enregistrée" });
      onClose();
    } catch {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue",
        variant: "destructive",
      });
    }
  };

  const responsibleContactId = watch("responsible_contact_id");
  const coordinatorContactId = watch("coordinator_contact_id");
  const technicalContactId = watch("technical_contact_id");
  const sectionTeamEnabled = watch("section_team_enabled");
  const sectionScheduleEnabled = watch("section_schedule_enabled");
  const sectionLogisticsEnabled = watch("section_logistics_enabled");
  const sectionTechnicalEnabled = watch("section_technical_enabled");
  const sectionCommunicationEnabled = watch("section_communication_enabled");

  const getContactData = (contactId?: string) => {
    if (!contactId || !cbmContacts) return null;
    const contact = cbmContacts.find((c) => c.id === contactId);
    if (!contact) return null;
    return {
      full_name: contact.full_name,
      phone: contact.phone,
      email: contact.email,
      role: contact.role,
    };
  };

  const pdfFormData = {
    ...formValues,
    responsible_contact: getContactData(responsibleContactId),
    coordinator_contact: getContactData(coordinatorContactId),
    technical_contact: getContactData(technicalContactId),
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-[95vw] w-[95vw] h-[90vh] p-0 gap-0"
        aria-describedby="technical-sheet-description"
      >
        <DialogDescription id="technical-sheet-description" className="sr-only">
          Formulaire de création/modification de fiche technique avec aperçu PDF
          en temps réel
        </DialogDescription>
        <div className="flex h-full">
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-semibold">
                    {existingSheet
                      ? "Modifier la fiche technique"
                      : "Créer une fiche technique"}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    {event?.name}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {isSaving && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Enregistrement...</span>
                    </div>
                  )}
                  {!isSaving && lastSaved && (
                    <div className="flex items-center gap-2 text-sm text-green-600">
                      <Save className="h-4 w-4" />
                      <span>
                        Enregistré à{" "}
                        {lastSaved.toLocaleTimeString("fr-FR", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <WizardProgressIndicator
                steps={steps}
                currentStep={currentStep}
                onStepClick={goToStep}
              />
            </div>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex-1 flex flex-col overflow-hidden"
            >
              <div className="flex-1 overflow-y-auto p-6">
                {currentStep === 0 && (
                  <div className="space-y-6 max-w-2xl">
                    <div>
                      <h3 className="text-lg font-semibold mb-4">
                        Contacts CBM
                      </h3>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Responsable CBM</Label>
                          <Select
                            value={responsibleContactId}
                            onValueChange={(value) =>
                              setValue("responsible_contact_id", value)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Sélectionner un responsable" />
                            </SelectTrigger>
                            <SelectContent>
                              {responsables.map((contact) => (
                                <SelectItem key={contact.id} value={contact.id}>
                                  {contact.full_name} - {contact.phone}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Coordinateur événementiel</Label>
                          <Select
                            value={coordinatorContactId}
                            onValueChange={(value) =>
                              setValue("coordinator_contact_id", value)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Sélectionner un coordinateur" />
                            </SelectTrigger>
                            <SelectContent>
                              {coordinateurs.map((contact) => (
                                <SelectItem key={contact.id} value={contact.id}>
                                  {contact.full_name} - {contact.phone}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Responsable technique</Label>
                          <Select
                            value={technicalContactId}
                            onValueChange={(value) =>
                              setValue("technical_contact_id", value)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Sélectionner un technicien" />
                            </SelectTrigger>
                            <SelectContent>
                              {techniciens.map((contact) => (
                                <SelectItem key={contact.id} value={contact.id}>
                                  {contact.full_name} - {contact.phone}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-4">
                        Informations financières
                      </h3>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="total_price">Prix total (€)</Label>
                          <Input
                            id="total_price"
                            type="number"
                            step="0.01"
                            {...register("total_price")}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="deposit_paid">
                            Acompte versé (€)
                          </Label>
                          <Input
                            id="deposit_paid"
                            type="number"
                            step="0.01"
                            {...register("deposit_paid")}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="balance_due">Solde dû (€)</Label>
                          <Input
                            id="balance_due"
                            type="number"
                            step="0.01"
                            {...register("balance_due")}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {currentStep === 1 && (
                  <div className="space-y-6 max-w-2xl">
                    <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg border">
                      <Checkbox
                        id="section_team_enabled"
                        checked={sectionTeamEnabled ?? true}
                        onCheckedChange={(checked: boolean) =>
                          setValue("section_team_enabled", !!checked)
                        }
                      />
                      <Label
                        htmlFor="section_team_enabled"
                        className="text-sm font-medium cursor-pointer"
                      >
                        Inclure cette section dans le PDF
                      </Label>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-4">
                        Composition de l'équipe
                      </h3>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="team_musicians_count">
                            Musiciens
                          </Label>
                          <Input
                            id="team_musicians_count"
                            type="number"
                            {...register("team_musicians_count")}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="team_technicians_count">
                            Techniciens
                          </Label>
                          <Input
                            id="team_technicians_count"
                            type="number"
                            {...register("team_technicians_count")}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="team_total">Total équipe</Label>
                          <Input
                            id="team_total"
                            type="number"
                            {...register("team_total")}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="team_specialization">
                        Spécialisation (ex: louange, évangélisation)
                      </Label>
                      <Input
                        id="team_specialization"
                        placeholder="louange"
                        {...register("team_specialization")}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="target_audience">Public cible</Label>
                      <Input
                        id="target_audience"
                        placeholder="Jeunes, familles, tout public..."
                        {...register("target_audience")}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="desired_ambiance">
                        Ambiance souhaitée
                      </Label>
                      <Input
                        id="desired_ambiance"
                        placeholder="Festive, recueillie, contemplative..."
                        {...register("desired_ambiance")}
                      />
                    </div>
                  </div>
                )}

                {currentStep === 2 && (
                  <div className="space-y-6 max-w-2xl">
                    <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg border">
                      <Checkbox
                        id="section_logistics_enabled"
                        checked={sectionLogisticsEnabled ?? true}
                        onCheckedChange={(checked: boolean) =>
                          setValue("section_logistics_enabled", !!checked)
                        }
                      />
                      <Label
                        htmlFor="section_logistics_enabled"
                        className="text-sm font-medium cursor-pointer"
                      >
                        Inclure cette section dans le PDF
                      </Label>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-4">
                        Hébergement
                      </h3>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="accommodation_count">
                              Nombre de personnes
                            </Label>
                            <Input
                              id="accommodation_count"
                              type="number"
                              {...register("accommodation_count")}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="accommodation_distance">
                              Distance max du lieu
                            </Label>
                            <Input
                              id="accommodation_distance"
                              {...register("accommodation_distance")}
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="accommodation_type">
                            Type hébergement
                          </Label>
                          <Input
                            id="accommodation_type"
                            {...register("accommodation_type")}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="accommodation_details">
                            Détails supplémentaires
                          </Label>
                          <Textarea
                            id="accommodation_details"
                            placeholder="Informations complémentaires..."
                            {...register("accommodation_details")}
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-4">
                        Transport & Repas
                      </h3>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="transport_note">Note transport</Label>
                          <Textarea
                            id="transport_note"
                            {...register("transport_note")}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="meals_during_event">
                            Repas pendant événement
                          </Label>
                          <Input
                            id="meals_during_event"
                            placeholder="Eau pour les musiciens..."
                            {...register("meals_during_event")}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="meals_after_event">
                            Repas après événement
                          </Label>
                          <Input
                            id="meals_after_event"
                            placeholder="Collation légère ou repas chaud..."
                            {...register("meals_after_event")}
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-4">Stockage</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="storage_location">Localisation</Label>
                          <Input
                            id="storage_location"
                            {...register("storage_location")}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="storage_duration">Durée</Label>
                          <Input
                            id="storage_duration"
                            placeholder="Du 5 au 7 juillet"
                            {...register("storage_duration")}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {currentStep === 3 && (
                  <div className="space-y-6 max-w-2xl">
                    <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg border">
                      <Checkbox
                        id="section_technical_enabled"
                        checked={sectionTechnicalEnabled ?? true}
                        onCheckedChange={(checked: boolean) =>
                          setValue("section_technical_enabled", !!checked)
                        }
                      />
                      <Label
                        htmlFor="section_technical_enabled"
                        className="text-sm font-medium cursor-pointer"
                      >
                        Inclure cette section dans le PDF
                      </Label>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-4">
                        Retours scène
                      </h3>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="in_ears_singers_count">
                              Chanteurs (In-Ears)
                            </Label>
                            <Input
                              id="in_ears_singers_count"
                              type="number"
                              {...register("in_ears_singers_count")}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="in_ears_musicians_count">
                              Musiciens
                            </Label>
                            <Input
                              id="in_ears_musicians_count"
                              type="number"
                              {...register("in_ears_musicians_count")}
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="audio_requirements">
                            Besoins audio
                          </Label>
                          <Textarea
                            id="audio_requirements"
                            placeholder="Sonorisation, micros..."
                            {...register("audio_requirements")}
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-4">Projection</h3>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="projection_equipment_type">
                            Type équipement
                          </Label>
                          <Input
                            id="projection_equipment_type"
                            placeholder="Écran LED ou rétro-projecteur"
                            {...register("projection_equipment_type")}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="projection_requirements">
                            Besoins projection
                          </Label>
                          <Textarea
                            id="projection_requirements"
                            {...register("projection_requirements")}
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-4">
                        Balances & Batterie
                      </h3>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="balances_duration">
                              Durée balances
                            </Label>
                            <Input
                              id="balances_duration"
                              {...register("balances_duration")}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="battery_provider">
                              Batterie fournie par
                            </Label>
                            <Input
                              id="battery_provider"
                              {...register("battery_provider")}
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="battery_specifications">
                            Spécifications batterie
                          </Label>
                          <Input
                            id="battery_specifications"
                            {...register("battery_specifications")}
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center space-x-2 mb-4 p-3 bg-slate-50 rounded-lg border">
                        <Checkbox
                          id="section_communication_enabled"
                          checked={sectionCommunicationEnabled ?? false}
                          onCheckedChange={(checked: boolean) =>
                            setValue("section_communication_enabled", !!checked)
                          }
                        />
                        <Label
                          htmlFor="section_communication_enabled"
                          className="text-sm font-medium cursor-pointer"
                        >
                          Inclure la section Communication dans le PDF
                        </Label>
                      </div>

                      <h3 className="text-lg font-semibold mb-4">
                        Communication
                      </h3>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="press_kit_url">
                            Dossier presse (URL)
                          </Label>
                          <Input
                            id="press_kit_url"
                            type="url"
                            {...register("press_kit_url")}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="visual_assets_url">
                            Visuels/Teasers (URL)
                          </Label>
                          <Input
                            id="visual_assets_url"
                            type="url"
                            {...register("visual_assets_url")}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {currentStep === 4 && (
                  <div className="space-y-6 max-w-2xl">
                    <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg border">
                      <Checkbox
                        id="section_schedule_enabled"
                        checked={sectionScheduleEnabled ?? true}
                        onCheckedChange={(checked: boolean) =>
                          setValue("section_schedule_enabled", !!checked)
                        }
                      />
                      <Label
                        htmlFor="section_schedule_enabled"
                        className="text-sm font-medium cursor-pointer"
                      >
                        Inclure cette section dans le PDF
                      </Label>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-4">
                        Horaires de la journée
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="arrival_time">Heure d'arrivée</Label>
                          <Input
                            id="arrival_time"
                            type="time"
                            {...register("arrival_time")}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="soundcheck_time">
                            Heure des balances
                          </Label>
                          <Input
                            id="soundcheck_time"
                            type="time"
                            {...register("soundcheck_time")}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="performance_start">
                            Début performance
                          </Label>
                          <Input
                            id="performance_start"
                            type="time"
                            {...register("performance_start")}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="performance_end">
                            Fin performance
                          </Label>
                          <Input
                            id="performance_end"
                            type="time"
                            {...register("performance_end")}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="teardown_time">
                            Heure de démontage
                          </Label>
                          <Input
                            id="teardown_time"
                            type="time"
                            {...register("teardown_time")}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-between p-6 border-t bg-slate-50">
                <Button
                  type="button"
                  variant="outline"
                  onClick={prevStep}
                  disabled={!canGoPrev}
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Précédent
                </Button>

                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={onClose}>
                    Annuler
                  </Button>
                  {canGoNext ? (
                    <Button type="button" onClick={nextStep}>
                      Suivant
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  ) : (
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Enregistrement...
                        </>
                      ) : (
                        "Terminer"
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </div>

          <div className="w-[50%] h-full">
            <LivePdfPreview
              formData={pdfFormData}
              event={
                event
                  ? {
                      name: event.name,
                      event_date: event.event_date,
                      event_time: event.event_time,
                      location: event.location,
                      event_type: event.event_type,
                      expected_attendees: event.expected_attendees,
                    }
                  : undefined
              }
              organization={
                event?.organizer ? { name: event.organizer.name } : undefined
              }
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
